// "What stands out" for a concessions game day (live: today so far; past: the analysis window).
// Plain-language findings about one stand's sections, ranked so the most important come first.
// Same card format as the Overview findings (OverviewInsights). Series are { [sectionId]: [{ t, y, o }] }
// with t on the venue's clock, the same series the page's tiles and charts use.
import React from 'react';
import { QUEUE_TIME_GOOD_MAX } from '../concessionTheme';
import { averageSeries, minutesOver, overflowSummary, overflowAcrossSections, formatClock, formatDuration, trend } from './liveMetrics';

const MINUTE = 60 * 1000;
// Section gaps this big or more are worth sending people to the shorter section
const GAP_MIN = 5;
// Transactions this much slower when queues are long count as a finding
const SLOW_TXN_SHARE = 0.2;
// Live trend: change over the last 15 minutes worth mentioning
const TREND_MIN = 2;
// Recovered: back under the line for at least this many minutes
const RECOVERED_FOR = 5;

const durationText = (m) => (m >= 60 ? formatDuration(m) : `${Math.round(m)} min`);
const relativeToStart = (t, start) => {
  if (start == null || t == null) return null;
  const m = Math.round((t - start) / MINUTE / 5) * 5;
  if (Math.abs(m) < 5) return 'right at game start';
  return m < 0 ? `${durationText(-m)} before game start` : `${durationText(m)} after game start`;
};
const section = (id) => `Section ${id}`;
const mean = (values) => (values.length ? values.reduce((a, b) => a + b, 0) / values.length : null);
const minuteKey = (t) => Math.floor(t / MINUTE) * MINUTE;

// The page's own rules (ConcessionLocationController's stats), so the findings agree with the cards:
// a section's value for a minute is its highest reading that minute; the stand's value is the average of
// its sections' values; a peak is the first minute that reaches the highest value.
const perMinuteMax = (series) => {
  const byMinute = new Map();
  series.forEach(({ t, y, o }) => {
    const k = minuteKey(t);
    const b = byMinute.get(k);
    if (!b) byMinute.set(k, { t: k, y, o: o ? 1 : 0 });
    else {
      b.y = Math.max(b.y, y);
      if (o) b.o = 1;
    }
  });
  return [...byMinute.values()].sort((a, b) => a.t - b.t);
};
const firstPeak = (series) => series.reduce((best, p) => (!best || p.y > best.y ? p : best), null);
const averageOfMax = (bySection) => {
  const byMinute = new Map();
  Object.values(bySection).forEach((series) => perMinuteMax(series).forEach(({ t, y, o }) => {
    const b = byMinute.get(t) || { t, sum: 0, n: 0, o: 0 };
    b.sum += y;
    b.n += 1;
    if (o) b.o = 1;
    byMinute.set(t, b);
  }));
  return [...byMinute.values()].sort((a, b) => a.t - b.t).map((b) => ({ t: b.t, y: b.sum / b.n, o: b.o }));
};

// Longest and shortest section at every minute with 2+ sections reporting (same as the page's delta)
const sectionGaps = (bySection) => {
  const byMinute = new Map();
  Object.entries(bySection).forEach(([id, series]) => perMinuteMax(series).forEach(({ t, y }) => {
    const b = byMinute.get(t) || [];
    b.push({ id, y });
    byMinute.set(t, b);
  }));
  return [...byMinute.entries()]
    .filter(([, list]) => list.length > 1)
    .sort((a, b) => a[0] - b[0])
    .map(([t, list]) => {
      const sorted = [...list].sort((a, b) => b.y - a.y);
      return { t, hi: sorted[0], lo: sorted[sorted.length - 1], gap: sorted[0].y - sorted[sorted.length - 1].y };
    });
};

// mainSeries / txnSeries: per section; isCountMode: people-count stands; gameStart: the schedule's start
// as a timestamp on the venue clock (or null); isLive: today so far. People-count stands use the page's
// "busy" levels: above countSectionBusyAt for one section, above countAvgBusyAt for the stand's average.
export const buildConcessionDayInsights = ({
  mainSeries, txnSeries, isCountMode, gameStart, isLive, countSectionBusyAt = 30, countAvgBusyAt = 40,
  formatTransactionTime = (v) => `${Math.round(v)}s`,
}) => {
  const ids = Object.keys(mainSeries).filter((id) => (mainSeries[id] || []).length > 0);
  if (!ids.length) return [];
  const avg = averageSeries(Object.fromEntries(ids.map((id) => [id, mainSeries[id]])));
  if (avg.length < 2) return [];
  // threshold: the stand's average; sectionThreshold: one section (the same for queues)
  const threshold = isCountMode ? countAvgBusyAt : QUEUE_TIME_GOOD_MAX;
  const sectionThreshold = isCountMode ? countSectionBusyAt : QUEUE_TIME_GOOD_MAX;
  const atOrAbove = !isCountMode;
  const isOver = (y) => (atOrAbove ? Math.round(y) >= threshold : y > threshold);
  const unit = isCountMode ? 'people' : 'min';
  // Over target includes overflowing readings (minutesOver counts them as over)
  const overWord = isCountMode ? 'busy' : `at least ${QUEUE_TIME_GOOD_MAX} mins or overflowing`;
  // The page's own names for it: "Time over target" / "Time busy"
  const overTime = isCountMode ? 'busy time' : 'time over target';
  const stillOver = isCountMode ? 'Still busy' : 'Still over target';
  const so = isLive ? ' so far' : '';
  // What the line measures: "The queue" / "The people count", or the average when there are sections
  const whole = ids.length > 1 ? (isCountMode ? 'The average count across sections' : 'The average queue across sections') : (isCountMode ? 'The people count' : 'The queue');
  const wholeLower = whole.charAt(0).toLowerCase() + whole.slice(1);
  const insights = [];

  // Overflow: the queue ran past the sensors, so the real queue was longer than recorded
  if (!isCountMode) {
    const perSection = ids.map((id) => ({ id, ...overflowSummary(mainSeries[id]) })).filter((s) => s.minutes > 0)
      .sort((a, b) => b.minutes - a.minutes);
    if (perSection.length) {
      // From the raw readings (any section overflowing), so a one-section stand matches its tile
      const total = overflowAcrossSections(mainSeries);
      const longest = total.stretches.reduce((best, st) => (!best || st.minutes > best.minutes ? st : best), null);
      const longestOverflow = longest ? { ...longest, ongoing: isLive && longest.ongoing } : null;
      insights.push({
        key: 'overflow',
        tone: 'bad',
        icon: 'exclamation-triangle-fill',
        score: 100,
        label: 'Overflow',
        text: (
          <>
            Queues were in overflow for <strong>{formatDuration(total.minutes)}</strong>{so}
            {longestOverflow && (total.episodes > 1 ? (
              <>
                {' '}({total.episodes} times), the longest stretch <strong>{formatDuration(longestOverflow.minutes)}</strong>
                {' '}({formatClock(longestOverflow.start)} – {longestOverflow.ongoing ? 'now' : formatClock(longestOverflow.end)})
              </>
            ) : (
              <> ({formatClock(longestOverflow.start)} – {longestOverflow.ongoing ? 'now' : formatClock(longestOverflow.end)})</>
            ))}
            {ids.length > 1 && (perSection.length === ids.length && perSection.every((x) => x.minutes === perSection[0].minutes)
              ? <>, at every section</>
              : perSection.length > 1 && perSection[1].minutes === perSection[0].minutes
                ? <>, mostly at <strong>{perSection.filter((x) => x.minutes === perSection[0].minutes).map((x) => section(x.id)).join(', ')}</strong> ({formatDuration(perSection[0].minutes)} each)</>
                : <>, mostly at <strong>{section(perSection[0].id)}</strong> ({formatDuration(perSection[0].minutes)})</>)}.
          </>
        ),
      });
    }
  }

  // Longest queues (or busiest section): the section over the line the longest
  const perSection = ids.map((id) => {
    const series = mainSeries[id];
    const peak = firstPeak(perMinuteMax(series));
    return { id, over: minutesOver(series, sectionThreshold, atOrAbove).minutes, peak, avg: mean(series.map((p) => p.y)) };
  });
  const worst = [...perSection].sort((a, b) => b.over - a.over || (b.peak?.y || 0) - (a.peak?.y || 0))[0];
  if (worst.over > 0) {
    const others = perSection.filter((s) => s.over > 0).length - 1;
    insights.push({
      key: 'worst',
      tone: 'bad',
      icon: 'hourglass-split',
      score: 90,
      label: ids.length > 1 ? (isCountMode ? 'Busiest section' : 'Longest queues') : (isCountMode ? 'Busy time' : 'Over target'),
      text: (
        <>
          <strong>{ids.length > 1 ? section(worst.id) : whole}</strong> was {overWord} for <strong>{formatDuration(worst.over)}</strong>{so},
          {' '}peaking at <strong>{Math.round(worst.peak.y)}{worst.peak.o ? '+' : ''} {unit}</strong> at {formatClock(worst.peak.t)}
          {others > 0 ? <>; {others} other section{others === 1 ? '' : 's'} {isCountMode ? (others === 1 ? 'was' : 'were') + ' busy too' : 'also went over'}</> : ''}.
        </>
      ),
    });
  } else {
    const top = [...perSection].sort((a, b) => (b.peak?.y || 0) - (a.peak?.y || 0))[0];
    insights.push({
      key: 'short',
      tone: 'good',
      icon: 'check-circle-fill',
      score: 45,
      label: isCountMode ? 'Never busy' : 'On target',
      text: isCountMode ? (
        <>No section went above <strong>{sectionThreshold} people</strong>{so}. The highest was {Math.round(top.peak.y)} at {ids.length > 1 ? section(top.id) : 'the stand'}.</>
      ) : (
        <>Every section stayed under <strong>{QUEUE_TIME_GOOD_MAX} min</strong>{so}. The longest was <strong>{Math.round(top.peak.y)} min</strong>{ids.length > 1 ? <> at {section(top.id)}</> : ''} ({formatClock(top.peak.t)}).</>
      ),
    });
  }

  // Section gap: one section much longer than another at the same time (send people to the shorter one)
  if (!isCountMode && ids.length > 1) {
    const gaps = sectionGaps(Object.fromEntries(ids.map((id) => [id, mainSeries[id]])));
    const biggest = gaps.reduce((best, g) => (!best || g.gap > best.gap ? g : best), null);
    if (biggest && Math.round(biggest.gap) >= GAP_MIN) {
      const wideMinutes = gaps.filter((g) => Math.round(g.gap) >= GAP_MIN).length;
      const now = isLive ? gaps[gaps.length - 1] : null;
      insights.push({
        key: 'gap',
        tone: 'info',
        icon: 'arrow-left-right',
        score: 75 + Math.min(10, Math.round(biggest.gap)),
        label: 'Section gap',
        text: (
          <>
            At {formatClock(biggest.t)}, <strong>{section(biggest.hi.id)}</strong> ({Math.round(biggest.hi.y)} min) was
            {' '}<strong>{Math.round(biggest.gap)} min</strong> longer than <strong>{section(biggest.lo.id)}</strong> ({Math.round(biggest.lo.y)} min).
            {' '}The gap was {GAP_MIN} min or more for <strong>{formatDuration(wideMinutes)}</strong>{so}
            {now && Math.round(now.gap) >= GAP_MIN
              ? <>; right now {section(now.lo.id)} is {Math.round(now.gap)} min shorter than {section(now.hi.id)}</>
              : ''}.
          </>
        ),
      });
    }
  }

  // When the queue peaked, relative to game start (the same peak as the "Peak average" card)
  const standByMinute = averageOfMax(Object.fromEntries(ids.map((id) => [id, mainSeries[id]])));
  const peak = firstPeak(standByMinute);
  if (peak && gameStart != null) {
    const when = relativeToStart(peak.t, gameStart);
    insights.push({
      key: 'timing',
      tone: 'info',
      icon: 'clock',
      score: 55,
      label: 'When it peaked',
      text: (
        <>
          {whole} peaked at <strong>{Math.round(peak.y)}{peak.o ? '+' : ''} {unit}</strong>
          {' '}at {formatClock(peak.t)}, <strong>{when}</strong>{isLive ? ' (so far)' : ''}.
        </>
      ),
    });
  }

  // Before game start vs in-game: where the time over the line happened
  if (gameStart != null) {
    const before = minutesOver(avg.filter((p) => p.t < gameStart), threshold, atOrAbove).minutes;
    const after = minutesOver(avg.filter((p) => p.t >= gameStart), threshold, atOrAbove).minutes;
    const hasBefore = avg.some((p) => p.t < gameStart);
    const hasAfter = avg.some((p) => p.t >= gameStart);
    if (before + after > 0 && hasBefore && hasAfter) {
      const share = Math.round((before / (before + after)) * 100);
      insights.push({
        key: 'split',
        tone: 'info',
        icon: 'pie-chart',
        score: 50,
        label: 'Before vs after start',
        text: share >= 60 || share <= 40 ? (
          <>
            <strong>{share >= 60 ? `${share}%` : `${100 - share}%`}</strong> of the {overTime} was
            {' '}<strong>{share >= 60 ? 'before game start' : 'after game start'}</strong>
            {' '}({formatDuration(before)} before, {formatDuration(after)} after{isLive ? ', so far' : ''}).
          </>
        ) : (
          <>The {overTime} was split evenly around game start ({formatDuration(before)} before, {formatDuration(after)} after{isLive ? ', so far' : ''}).</>
        ),
      });
    }
  }

  // Recovery. "OK" = under the line and not overflowing (an overflowing reading is a minimum, so it
  // counts as over). Live games are anchored on the last time it was OK, so a recovery isn't forgotten
  // when the queue goes back over; past games look at the day's peak.
  const okPoint = (p) => !isOver(p.y) && !p.o;
  const underText = isCountMode ? `to ${threshold} people or fewer` : `under ${QUEUE_TIME_GOOD_MAX} min`;
  const recoveredText = (stretchPeak, back) => {
    const minutes = Math.round((back.t - stretchPeak.t) / MINUTE);
    return {
      minutes,
      text: (
        <>
          After peaking at {formatClock(stretchPeak.t)}, {wholeLower} took
          {' '}<strong>{durationText(minutes)}</strong> to get back {underText} ({formatClock(back.t)})
        </>
      ),
    };
  };

  if (isLive && peak && isOver(peak.y)) {
    const latest = standByMinute[standByMinute.length - 1];
    // Index of the last OK reading
    let lastOk = -1;
    for (let i = standByMinute.length - 1; i >= 0; i -= 1) {
      if (okPoint(standByMinute[i])) { lastOk = i; break; }
    }
    if (!okPoint(latest)) {
      // Over the line now: since the reading after the last OK one (or since it first went over)
      const firstOver = standByMinute.findIndex((p) => !okPoint(p));
      const since = lastOk >= 0 && lastOk > firstOver ? standByMinute[lastOk + 1] : null;
      if (since) {
        // It recovered earlier, then went back over: just since when, and for how long
        const minutes = Math.round((latest.t - since.t) / MINUTE);
        insights.push({
          key: 'recovery',
          tone: 'bad',
          icon: 'arrow-down-right-circle',
          score: minutes >= 5 ? 85 : 70,
          label: isCountMode ? 'Busy' : 'Over target',
          text: (
            <>{isCountMode ? 'Busy' : 'Over target'} since {formatClock(since.t)} (<strong>{durationText(Math.max(1, minutes))}</strong> so far).</>
          ),
        });
      } else {
        // Never back to OK since it went over
        const minutes = Math.round((latest.t - peak.t) / MINUTE);
        if (minutes >= 5) {
          insights.push({
            key: 'recovery',
            tone: 'bad',
            icon: 'arrow-down-right-circle',
            score: 85,
            label: 'Not recovered yet',
            text: <>{stillOver} <strong>{durationText(minutes)}</strong> after peaking at {formatClock(peak.t)}.</>,
          });
        }
      }
    } else {
      // OK now: the most recent stretch over the line, its peak and when it got back to OK
      let lastOver = -1;
      for (let i = standByMinute.length - 1; i >= 0; i -= 1) {
        if (!okPoint(standByMinute[i])) { lastOver = i; break; }
      }
      if (lastOver >= 0) {
        let start = lastOver;
        while (start > 0 && !okPoint(standByMinute[start - 1])) start -= 1;
        const stretch = standByMinute.slice(start, lastOver + 1);
        const stretchPeak = firstPeak(stretch);
        if (stretchPeak && isOver(stretchPeak.y)) {
          const back = standByMinute[lastOver + 1];
          const r = recoveredText(stretchPeak, back);
          insights.push({
            key: 'recovery',
            tone: r.minutes >= 30 ? 'bad' : 'info',
            icon: 'arrow-down-right-circle',
            score: 60 + Math.min(20, Math.round(r.minutes / 3)),
            label: 'Recovery',
            text: <>{r.text}.</>,
          });
        }
      }
    }
  } else if (peak && isOver(peak.y)) {
    // Past game: from the day's peak back under the line, staying there for RECOVERED_FOR minutes
    const after = standByMinute.filter((p) => p.t > peak.t);
    const back = after.find((p) => okPoint(p) && after.every((q) => q.t <= p.t || q.t > p.t + RECOVERED_FOR * MINUTE || okPoint(q)));
    if (back) {
      const r = recoveredText(peak, back);
      const again = standByMinute.find((p) => p.t > back.t && !okPoint(p));
      insights.push({
        key: 'recovery',
        tone: r.minutes >= 30 ? 'bad' : 'info',
        icon: 'arrow-down-right-circle',
        score: 60 + Math.min(20, Math.round(r.minutes / 3)),
        label: 'Recovery',
        text: <>{r.text}{again ? <>, then went back over at {formatClock(again.t)}</> : ''}.</>,
      });
    }
  }

  // Slow transactions while queues were long
  if (!isCountMode && txnSeries) {
    const avgTxn = averageSeries(Object.fromEntries(ids.filter((id) => txnSeries[id]?.length).map((id) => [id, txnSeries[id]])));
    const queueAt = new Map(avg.map((p) => [minuteKey(p.t), p]));
    const long = [];
    const short = [];
    avgTxn.forEach((p) => {
      const q = queueAt.get(minuteKey(p.t));
      if (!q || !(p.y > 0)) return;
      (isOver(q.y) || q.o ? long : short).push(p.y);
    });
    if (long.length >= 5 && short.length >= 5) {
      const l = mean(long);
      const s = mean(short);
      const slower = (l - s) / s;
      if (slower >= SLOW_TXN_SHARE) {
        insights.push({
          key: 'txn',
          tone: 'info',
          icon: 'stopwatch',
          score: 65,
          label: 'Slower transactions',
          text: (
            <>
              Transactions took <strong>{Math.round(slower * 100)}% longer</strong> while queues were {overWord}
              {' '}({formatTransactionTime(l)} vs {formatTransactionTime(s)}), so slower service likely made the queues longer.
            </>
          ),
        });
      }
    }
  }

  // Live: which way it's heading right now
  if (isLive) {
    const delta = trend(avg, 15);
    if (delta != null && Math.abs(delta) >= TREND_MIN) {
      const rising = delta > 0;
      insights.push({
        key: 'now',
        tone: rising ? 'bad' : 'good',
        icon: rising ? 'graph-up-arrow' : 'graph-down-arrow',
        score: rising ? 80 : 40,
        label: rising ? 'Rising now' : 'Easing now',
        text: (
          <>
            {whole} is <strong>{rising ? 'up' : 'down'} {Math.abs(Math.round(delta))} {unit}</strong>
            {' '}over the last 15 minutes, now <strong>{Math.round(avg[avg.length - 1].y)} {unit}</strong>.
          </>
        ),
      });
    }
  }

  return insights.sort((a, b) => b.score - a.score).slice(0, 6);
};

